import type { RuntimeConfig } from './config';
import type { SeriesRef, StudyRef } from './dicomweb';
import { imageIdsForSeries, initialImageIndex, type LoaderContext } from './loader';
import { isReferenceImageId, referenceImageIds, seriesNeedsReference } from './display/reference';
import { isImageSeries } from './study';
import { installDisplayFeatures, isSpecialSeries, showSpecialSeries } from './display';
import type { Layout, ShellEventName, ShellEvents, ShellRegions, ToastKind, ViewerShell } from './shell';
import { Viewport } from './viewport';
import { isNetworkFailure } from './net';

export type { Layout } from './shell';

const TOAST_MS = 6000;

type Listener = (detail: never) => void;

/**
 * The screen shell: regions, viewport grid, focus, full screen, drawers, toasts and events.
 * Everything a user sees inside the regions (toolbar, browser, overlays, menus…) is a feature module.
 */
export class ViewerApp implements ViewerShell {
  readonly regions: ShellRegions;
  private viewportList: Viewport[] = [];
  private active = 0;
  private currentLayout: Layout = { rows: 1, columns: 1 };
  private fullScreen: number | undefined;
  private studyList: StudyRef[] = [];
  private readonly imageIdCache = new Map<string, string[]>();
  /** Series whose frames failed to decode here: shown as server-rendered reference images. */
  private readonly referenceSeries = new Set<string>();
  private readonly listeners = new Map<ShellEventName, Set<Listener>>();
  private readonly body: HTMLElement;
  private navigationGuard: ((proceed: () => void) => void) | null = null;

  constructor(
    private readonly root: HTMLElement,
    readonly config: RuntimeConfig,
    readonly ctx: LoaderContext,
  ) {
    root.classList.add('viewer');
    const topNav = this.el('div', 'top-nav', root);
    topNav.id = 'top-nav';
    const toolbar = this.el('div', 'main-toolbar', topNav);
    toolbar.id = 'main-toolbar';
    const info = this.el('div', 'toolbar-info', root);
    info.id = 'toolbar-info';
    this.body = this.el('div', 'viewer-body', root);
    const leftDrawer = this.el('aside', 'studies-drawer', this.body);
    leftDrawer.id = 'studies-drawer';
    const main = this.el('main', 'viewer-main', this.body);
    const grid = this.el('div', 'viewer-grid', main);
    grid.id = 'viewer-layout';
    const rightDrawer = this.el('aside', 'annotations-drawer', this.body);
    rightDrawer.id = 'annotations-drawer';
    const overlayLayer = this.el('div', 'overlay-layer', root);
    this.regions = { topNav, toolbar, info, leftDrawer, grid, rightDrawer, overlayLayer };
    this.setRightDrawer(false);
    new ResizeObserver(() => this.viewportList.forEach((vp) => vp.resize())).observe(grid);
  }

  private el<K extends keyof HTMLElementTagNameMap>(tag: K, className: string, parent: HTMLElement): HTMLElementTagNameMap[K] {
    const node = document.createElement(tag);
    node.className = className;
    parent.appendChild(node);
    return node;
  }

  showMessage(text: string): void {
    this.regions.grid.replaceChildren();
    const message = this.el('div', 'viewer-message', this.regions.grid);
    message.textContent = text;
  }

  /** Sets the opened study; feature modules install after this and before open(). */
  begin(study: StudyRef): void {
    this.studyList = [study];
  }

  /** Builds the 1x1 grid and shows the first series; modules react through shell events. */
  async open(): Promise<void> {
    installDisplayFeatures(this);
    this.setLayout({ rows: 1, columns: 1 });
  }

  // --- ViewerShell -----------------------------------------------------------------------------

  study(): StudyRef {
    return this.studyList[0];
  }

  studies(): StudyRef[] {
    return this.studyList;
  }

  addStudy(study: StudyRef): void {
    if (this.studyList.some((s) => s.studyInstanceUID === study.studyInstanceUID)) return;
    this.studyList = [...this.studyList, study];
    this.emit('studies-changed', { studies: this.studyList });
  }

  viewports(): Viewport[] {
    return this.viewportList;
  }

  activeIndex(): number {
    return this.active;
  }

  activeViewport(): Viewport | undefined {
    return this.viewportList[this.active];
  }

  activate(index: number): void {
    if (index < 0 || index >= this.viewportList.length) return;
    const changed = index !== this.active;
    this.active = index;
    this.viewportList.forEach((vp, i) => vp.container.classList.toggle('active', i === index));
    if (changed) this.emit('active-changed', { index, viewport: this.activeViewport() });
  }

  layout(): Layout {
    return this.currentLayout;
  }

  setLayout(layout: Layout): void {
    const study = this.study();
    if (!study) return;
    const previous = this.viewportList.map((vp) => vp.currentStack?.series ?? vp.customSeries ?? null);
    this.setFullScreen(undefined);
    this.viewportList.forEach((vp) => vp.destroy());
    const { grid } = this.regions;
    grid.replaceChildren();
    grid.style.gridTemplateRows = `repeat(${layout.rows}, minmax(0, 1fr))`;
    grid.style.gridTemplateColumns = `repeat(${layout.columns}, minmax(0, 1fr))`;
    this.currentLayout = layout;
    const count = layout.rows * layout.columns;
    this.viewportList = Array.from({ length: count }, (_, index) => this.createCell(index, study));
    this.active = Math.min(this.active, count - 1);
    this.viewportList.forEach((vp, i) => vp.container.classList.toggle('active', i === this.active));
    this.emit('layout-changed', { layout, viewports: this.viewportList });
    this.emit('active-changed', { index: this.active, viewport: this.activeViewport() });
    // Keep what was on screen; fill new cells with the next series of the study.
    const viewable = this.studyList.flatMap((s) => s.series).filter(isImageSeries);
    // Image stacks first; PDFs, videos and reports fill cells only after them (or when there is nothing else).
    const allSeries = [...viewable.filter((s) => !isSpecialSeries(s)), ...viewable.filter(isSpecialSeries)];
    this.viewportList.forEach((_, index) => {
      const series = previous[index] ?? allSeries[index] ?? null;
      if (series) void this.showSeriesIn(index, series);
    });
  }

  private createCell(index: number, study: StudyRef): Viewport {
    const cell = this.el('div', 'viewport-cell', this.regions.grid);
    cell.dataset.index = String(index);
    const focus = () => this.activate(index);
    cell.addEventListener('mousedown', focus, true);
    cell.addEventListener('wheel', focus, { capture: true, passive: true });
    cell.addEventListener('touchstart', focus, { capture: true, passive: true });
    cell.addEventListener('dblclick', () => this.setFullScreen(this.fullScreen === index ? undefined : index));
    cell.addEventListener('dragover', (event) => event.preventDefault());
    cell.addEventListener('drop', (event) => {
      event.preventDefault();
      const uid = event.dataTransfer?.getData('text/series-uid');
      const series = this.findSeries(uid);
      if (series) this.guard(() => void this.showSeriesIn(index, series));
    });
    const viewport = new Viewport(cell, study, this.ctx);
    viewport.onImageRendered = (vp) => this.emit('image-rendered', { viewport: vp });
    return viewport;
  }

  findSeries(seriesUID: string | undefined): SeriesRef | undefined {
    if (!seriesUID) return undefined;
    for (const study of this.studyList) {
      const series = study.series.find((s) => s.seriesInstanceUID === seriesUID);
      if (series) return series;
    }
    return undefined;
  }

  imageIdsFor(series: SeriesRef): string[] {
    // Keyed by the instance set too: the browser shows derived tiles (one multi-frame instance,
    // the single-frame group) that share their parent's series UID.
    const key = this.imageIdKey(series);
    let ids = this.imageIdCache.get(key);
    if (!ids) {
      ids = this.referenceSeries.has(key) || seriesNeedsReference(series)
        ? referenceImageIds(this.ctx, series)
        : imageIdsForSeries(this.ctx, series);
      this.imageIdCache.set(key, ids);
    }
    return ids;
  }

  private imageIdKey(series: SeriesRef): string {
    return `${series.seriesInstanceUID}|${series.instances.map((i) => i.sopInstanceUID).join(',')}`;
  }

  async showSeriesIn(index: number, series: SeriesRef, startIndex?: number, networkRetried = false): Promise<void> {
    const viewport = this.viewportList[index];
    if (!viewport) return;
    if (isSpecialSeries(series)) {
      await showSpecialSeries(this, index, series);
      return;
    }
    const imageIds = this.imageIdsFor(series);
    const study = this.studyList.find((s) => s.studyInstanceUID === series.studyInstanceUID) ?? this.study();
    viewport.setStudy(study);
    const start = startIndex ?? initialImageIndex(series.modality, imageIds.length);
    try {
      await viewport.showSeries(series, imageIds, start);
    } catch (error) {
      if (isNetworkFailure(error)) {
        // No answer (idle timeout, dropped connection): the same image once more, then a visible error.
        // Not a decode problem, so no reference-image fallback.
        if (!networkRetried) {
          console.warn('[viewer] series got no answer, retrying once', series.seriesInstanceUID);
          await this.showSeriesIn(index, series, start, true);
          return;
        }
        console.error('[viewer] series failed (no answer from the server)', series.seriesInstanceUID, error);
        viewport.showError('Error al cargar la serie', 'por favor seleccione otra serie');
        return;
      }
      // A frame the browser cannot decode: show the server-rendered reference instead (once).
      const key = this.imageIdKey(series);
      if (isReferenceImageId(imageIds[0]) || this.referenceSeries.has(key)) {
        console.error('[viewer] series failed', series.seriesInstanceUID, error);
        viewport.showError('Error al cargar la serie', 'por favor seleccione otra serie');
        return;
      }
      console.warn('[viewer] decode failed, falling back to the reference image', series.seriesInstanceUID, error);
      this.referenceSeries.add(key);
      this.imageIdCache.delete(key);
      await this.showSeriesIn(index, series, start);
      return;
    }
    viewport.setReferenceMode(isReferenceImageId(imageIds[0]));
    if (this.viewportList[index] === viewport) this.emit('series-shown', { index, viewport, series });
  }

  setFullScreen(index: number | undefined): void {
    this.fullScreen = index;
    this.regions.grid.classList.toggle('full-screen', index !== undefined);
    this.root.classList.toggle('full-screen', index !== undefined);
    this.viewportList.forEach((vp, i) => vp.container.classList.toggle('full-screen-cell', i === index));
    if (index !== undefined) this.activate(index);
    this.emit('full-screen-changed', { index });
    requestAnimationFrame(() => this.viewportList.forEach((vp) => vp.resize()));
  }

  fullScreenIndex(): number | undefined {
    return this.fullScreen;
  }

  setLeftDrawer(open: boolean): void {
    const changed = this.isLeftDrawerOpen() !== open;
    this.body.classList.toggle('left-closed', !open);
    requestAnimationFrame(() => this.viewportList.forEach((vp) => vp.resize()));
    if (changed) this.emit('drawer-changed', { side: 'left', open });
  }

  isLeftDrawerOpen(): boolean {
    return !this.body.classList.contains('left-closed');
  }

  setRightDrawer(open: boolean): void {
    const changed = this.isRightDrawerOpen() !== open;
    this.body.classList.toggle('right-open', open);
    requestAnimationFrame(() => this.viewportList.forEach((vp) => vp.resize()));
    if (changed) this.emit('drawer-changed', { side: 'right', open });
  }

  isRightDrawerOpen(): boolean {
    return this.body.classList.contains('right-open');
  }

  toast(message: string, kind: ToastKind = 'info'): void {
    const toast = this.el('div', `toast toast-${kind}`, this.regions.overlayLayer);
    toast.setAttribute('role', 'status');
    toast.textContent = message;
    setTimeout(() => toast.remove(), TOAST_MS);
  }

  guard(action: () => void): void {
    if (this.navigationGuard) this.navigationGuard(action);
    else action();
  }

  setNavigationGuard(check: ((proceed: () => void) => void) | null): void {
    this.navigationGuard = check;
  }

  on<E extends ShellEventName>(event: E, listener: (detail: ShellEvents[E]) => void): () => void {
    let set = this.listeners.get(event);
    if (!set) {
      set = new Set();
      this.listeners.set(event, set);
    }
    set.add(listener as Listener);
    return () => set?.delete(listener as Listener);
  }

  emit<E extends ShellEventName>(event: E, detail: ShellEvents[E]): void {
    this.listeners.get(event)?.forEach((listener) => {
      try {
        (listener as (d: ShellEvents[E]) => void)(detail);
      } catch (error) {
        console.error(`[viewer] ${event} listener failed`, error);
      }
    });
  }
}
